import type { Position } from "../types/grid.js";

function key(pos: Position): string {
  return `${pos.x},${pos.z}`;
}

/**
 * Tracks which entity instanceIds occupy each grid cell.
 *
 * The data shape is plural-ready: a cell maps to a *set* of IDs, not
 * a single slot. v1 enforces at most one (medium-size) enemy per cell,
 * but v2 will allow up to four small enemies in a 2×2 sub-grid and
 * large entities spanning multiple cells. Callers use {@link canAccept}
 * to gate placement against whatever maximum the current rules demand.
 *
 * Spec: `specs/enemies-and-ai.md` §Monster size. Owned by the
 * enemies-and-ai module; pathfinder uses the same store to veto moves
 * into occupied cells.
 */
export class CellOccupancy {
  private readonly byCell = new Map<string, Set<string>>();
  private readonly byId = new Map<string, string>();

  /**
   * Place `instanceId` at `pos`. If the instance is already registered
   * elsewhere it is first removed from that cell — callers can treat
   * this as "upsert" and not track previous positions themselves.
   */
  place(instanceId: string, pos: Position): void {
    const k = key(pos);
    const existing = this.byId.get(instanceId);
    if (existing === k) return;
    if (existing !== undefined) this.removeFromCell(instanceId, existing);

    let set = this.byCell.get(k);
    if (!set) {
      set = new Set<string>();
      this.byCell.set(k, set);
    }
    set.add(instanceId);
    this.byId.set(instanceId, k);
  }

  /** Remove `instanceId` entirely. No-op if unknown. */
  remove(instanceId: string): void {
    const k = this.byId.get(instanceId);
    if (k === undefined) return;
    this.removeFromCell(instanceId, k);
    this.byId.delete(instanceId);
  }

  /** IDs at the given cell. Returns a fresh array; callers may mutate freely. */
  at(pos: Position): readonly string[] {
    const set = this.byCell.get(key(pos));
    return set ? [...set] : [];
  }

  /** Any entity currently in the cell? */
  isOccupied(pos: Position): boolean {
    const set = this.byCell.get(key(pos));
    return set !== undefined && set.size > 0;
  }

  /** Number of entities in the cell. */
  countAt(pos: Position): number {
    return this.byCell.get(key(pos))?.size ?? 0;
  }

  /** Current cell of `instanceId`, or null if unknown. */
  positionOf(instanceId: string): Position | null {
    const k = this.byId.get(instanceId);
    if (k === undefined) return null;
    const [x, z] = k.split(",").map((n) => Number.parseInt(n, 10));
    if (x === undefined || z === undefined || Number.isNaN(x) || Number.isNaN(z)) return null;
    return { x, z };
  }

  /** True iff `instanceId` is currently placed. */
  has(instanceId: string): boolean {
    return this.byId.has(instanceId);
  }

  /** Total number of tracked entities. */
  size(): number {
    return this.byId.size;
  }

  /** Forget everything. */
  clear(): void {
    this.byCell.clear();
    this.byId.clear();
  }

  /**
   * Can another entity enter `pos` without exceeding `maxOccupants`?
   * Default 1 matches v1's "one medium enemy per cell" rule; v2 will
   * pass 4 for small-size packs.
   */
  canAccept(pos: Position, maxOccupants: number = 1): boolean {
    const set = this.byCell.get(key(pos));
    return (set?.size ?? 0) < maxOccupants;
  }

  private removeFromCell(instanceId: string, k: string): void {
    const set = this.byCell.get(k);
    if (!set) return;
    set.delete(instanceId);
    if (set.size === 0) this.byCell.delete(k);
  }
}
